import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Stack, Chip, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControlLabel, Switch,
} from '@mui/material';
import {
  Sync as SyncIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { calendarApi, calendarSourcesApi } from '../api/services';
import type { CalendarEvent, CalendarSource } from '../types/models';

export default function CalendarPage() {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const loadEvents = async (date: string) => {
    setLoading(true);
    try {
      setEvents(await calendarApi.getByDate(date));
    } catch (err) {
      console.error('Failed to load events', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadEvents(selectedDate); }, [selectedDate]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const synced = await calendarApi.sync();
      setSyncMessage({ type: 'success', text: `Synced ${synced.length} events from ICS feed` });
      loadEvents(selectedDate);
    } catch (err: any) {
      const data = err.response?.data;
      const msg = typeof data === 'string' ? data : data?.error || data?.message || 'Sync failed — check ICS URL in preferences';
      setSyncMessage({ type: 'error', text: typeof msg === 'string' ? msg : 'Sync failed — check ICS URL in preferences' });
    } finally {
      setSyncing(false);
    }
  };

  const meetingMinutes = events
    .filter(e => !e.isAllDay)
    .reduce((sum, e) => sum + (new Date(e.endTime).getTime() - new Date(e.startTime).getTime()) / 60000, 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4">Calendar</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            type="date"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="outlined" startIcon={<SyncIcon />} onClick={handleSync} disabled={syncing}>
            {syncing ? 'Syncing...' : 'Sync ICS'}
          </Button>
        </Stack>
      </Box>

      {syncMessage && <Alert severity={syncMessage.type} sx={{ mb: 2 }} onClose={() => setSyncMessage(null)}>{syncMessage.text}</Alert>}

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ display: 'flex', gap: 3 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Events</Typography>
            <Typography variant="h6">{events.length}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Meeting Time</Typography>
            <Typography variant="h6">{Math.round(meetingMinutes / 60 * 10) / 10}h</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Priority Meetings</Typography>
            <Typography variant="h6" color="error.main">{events.filter(e => e.isPriorityMeeting || e.isPrivate).length}</Typography>
          </Box>
        </CardContent>
      </Card>

      <CalendarSourcesCard />

      <Stack spacing={1} sx={{ mt: 3 }}>
        {events.map((event) => (
          <Card key={event.id} sx={{
            borderLeft: 3,
            borderColor: event.isPriorityMeeting || event.isPrivate ? 'error.main' : event.isAllDay ? 'warning.main' : 'secondary.main',
          }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', minWidth: 110, color: 'text.secondary' }}>
                  {event.isAllDay ? 'All day' : `${formatTime(event.startTime)} – ${formatTime(event.endTime)}`}
                </Typography>
                <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>{event.title}</Typography>
                <Stack direction="row" spacing={0.5}>
                  {(event.isPriorityMeeting || event.isPrivate) && (
                    <Chip icon={<WarningIcon />} label="Priority" size="small" color="error" variant="outlined" sx={{ height: 24 }} />
                  )}
                  {event.isPrivate && <Chip label="Private" size="small" variant="outlined" sx={{ height: 24 }} />}
                </Stack>
              </Box>
              {event.location && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: '126px' }}>{event.location}</Typography>
              )}
            </CardContent>
          </Card>
        ))}
        {events.length === 0 && !loading && (
          <Card sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No calendar events for this date</Typography>
          </Card>
        )}
      </Stack>
    </Box>
  );
}

function CalendarSourcesCard() {
  const [sources, setSources] = useState<CalendarSource[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<Partial<CalendarSource> | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);

  const loadSources = async () => {
    try {
      setSources(await calendarSourcesApi.getAll());
    } catch (err) {
      console.error('Failed to load calendar sources', err);
    }
  };

  useEffect(() => { loadSources(); }, []);

  const handleSave = async () => {
    if (!editItem?.name || !editItem?.icsUrl) return;
    try {
      if (editItem.id) {
        await calendarSourcesApi.update(editItem.id, editItem);
      } else {
        await calendarSourcesApi.create(editItem);
      }
      setDialogOpen(false);
      setEditItem(null);
      loadSources();
    } catch (err) {
      console.error('Failed to save calendar source', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Remove this calendar source?')) return;
    try {
      await calendarSourcesApi.delete(id);
      loadSources();
    } catch (err) {
      console.error('Failed to delete', err);
    }
  };

  const handleSync = async (id: string) => {
    setSyncingId(id);
    try {
      await calendarSourcesApi.sync(id);
      loadSources();
    } catch (err) {
      console.error('Failed to sync', err);
    } finally {
      setSyncingId(null);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">Calendar Sources</Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditItem({ name: '', icsUrl: '', isActive: true });
              setDialogOpen(true);
            }}
          >
            Add Source
          </Button>
        </Box>
        {sources.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No calendar sources configured</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>ICS URL</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Synced</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sources.map((source) => (
                  <TableRow key={source.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{source.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {source.icsUrl.length > 50 ? source.icsUrl.substring(0, 50) + '...' : source.icsUrl}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={source.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={source.isActive ? 'success' : 'default'}
                        variant="outlined"
                        sx={{ height: 22 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {source.lastSyncedAt
                          ? format(new Date(source.lastSyncedAt), 'dd MMM HH:mm')
                          : 'Never'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleSync(source.id)}
                        disabled={syncingId === source.id}
                        title="Sync"
                      >
                        <SyncIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => { setEditItem(source); setDialogOpen(true); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(source.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editItem?.id ? 'Edit Calendar Source' : 'Add Calendar Source'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Name"
                fullWidth
                value={editItem?.name || ''}
                onChange={e => setEditItem(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Work Calendar"
              />
              <TextField
                label="ICS URL"
                fullWidth
                value={editItem?.icsUrl || ''}
                onChange={e => setEditItem(prev => ({ ...prev, icsUrl: e.target.value }))}
                placeholder="https://example.com/calendar.ics"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editItem?.isActive ?? true}
                    onChange={e => setEditItem(prev => ({ ...prev, isActive: e.target.checked }))}
                  />
                }
                label="Active"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSave}>Save</Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}
